/* eslint-disable no-restricted-syntax */
import React, { useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { IoIosClose } from 'react-icons/io';
import { FaCopy } from 'react-icons/fa';
import copyTextToClipboard from 'src/pages/ViewJob/utils';

export interface TabInfo {
  label: string;
  to: string;
  Icon?: React.ComponentType<any>;
  iconPosition?: 'top' | 'bottom' | 'start' | 'end';
  closeable?: boolean;
  jobId?: string | number,
  jobOrderId?: string | number,
}

type TabClasses = {
  root?: string;
  activeRoute?: string;
  container?: string;
};

interface MultiTabProps {
  tabs: TabInfo[];
  classes?: TabClasses;
  showHighLight?: boolean;
  scrollButtons?: boolean;
  variant?: 'standard' | 'scrollable' | 'fullWidth';
  onTabClose?: (id: number | string | undefined) => void;
}

const MultiTab = function MultiTab(props: MultiTabProps) {
  const {
    tabs,
    classes,
    showHighLight,
    scrollButtons,
    variant,
    onTabClose,
  } = props;

  const location = useLocation();
  const { pathname } = location;
  const tabValue = tabs.find((tab) => pathname.startsWith(tab.to));

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState<number | undefined>(undefined);

  const [copiedJobId, setCopiedJobId] = React.useState(false);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === containerRef.current) {
          setContainerWidth(entry.contentRect.width);
        }
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      setContainerWidth(containerRef.current.getBoundingClientRect().width);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div ref={containerRef}>
      <Box
        className={classes?.container}
        sx={{
          borderBottom: 1,
          borderColor: '#E4E4E4',
          width: '70%',
          maxWidth: (containerWidth || 500) - 5,
        }}
      >
        <Tabs
          value={showHighLight ? tabValue?.to : undefined}
          scrollButtons={scrollButtons}
          variant={variant}
        >
          {tabs.map((tab: TabInfo) => {
            const {
              label,
              to,
              Icon,
              iconPosition = 'end',
              closeable = false,
              jobOrderId,
            } = tab;

            const copyToClipboard = (text: string) => {
              copyTextToClipboard(text);
              setCopiedJobId(true);
              setTimeout(() => {
                setCopiedJobId(false);
              }, 1000);
            };

            const currentRoute = pathname.startsWith(to);
            return (
              <Tab
                key={jobOrderId || 0}
                value={to}
                className={
                  twMerge(
                    classes?.root,
                    currentRoute ? classes?.activeRoute : '',
                    'pb-0',
                    closeable ? 'pr-0' : '',
                  )
                }
                label={(
                  <span>
                    {label}
                    &nbsp;&nbsp;&nbsp;
                    {closeable && (
                      <>
                        <Tooltip open={copiedJobId} title="Copied" followCursor>
                          <IconButton
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              copyToClipboard(label);
                            }}
                          >
                            <FaCopy
                              color={currentRoute ? '#F97171' : ''}
                              className="text-xs"
                            />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          component="div"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (onTabClose) {
                              onTabClose(jobOrderId);
                            }
                          }}
                        >
                          <IoIosClose
                            color={currentRoute ? '#F97171' : ''}
                          />
                        </IconButton>
                      </>
                    )}
                  </span>
                )}
                to={to}
                component={Link}
                icon={Icon ? <Icon /> : undefined}
                iconPosition={iconPosition}
              />
            );
          })}
        </Tabs>
      </Box>
      <Outlet />
    </div>
  );
};

MultiTab.defaultProps = {
  classes: {
    root: '',
    activeRoute: 'text-[14px] text-[#F97171] font-bold',
  },
  showHighLight: true,
  scrollButtons: false,
  variant: 'scrollable',
  onTabClose: () => {},
};

MultiTab.propTypes = {

};

export default MultiTab;
